import type { ReactNode } from 'react';
import { DropdownMenu as RadixDropdownMenu } from 'radix-ui';

export interface DropdownMenuItem {
  label: string;
  onSelect: () => void;
  destructive?: boolean;
  disabled?: boolean;
}

export interface DropdownMenuProps {
  trigger: ReactNode;
  items: DropdownMenuItem[];
}

/**
 * Generic action menu (ui-guidelines.md section 13), mirroring the Radix usage
 * already inline in `NavDropdown`/`AppNavbar` (`DropdownMenu.Root` / `Trigger
 * asChild` / `Portal` / `Content` / `Item`) so both agree on the same API.
 * Extracted as its own primitive rather than a third hand-rolled copy; folding
 * those two call sites onto this component is left to a follow-up so this
 * diff stays reviewable.
 */
// `admin-dark:border` adds the width only in dark mode: the light menu is
// defined by its shadow (as it always has been), the dark menu by a hairline.
const CONTENT_CLASS =
  'bg-surface-overlay shadow-xl admin-dark:border admin-dark:border-border-strong';

// `data-disabled:hover:bg-transparent` (plus `data-disabled:data-highlighted:bg-transparent`
// for keyboard-navigated hover-equivalent highlighting) suppresses the hover/highlight
// background on disabled items - mnr-standards: "Disabled elements must ... suppress hover
// styles." Text color is already muted by the shared `data-disabled:opacity-50` below.
//
// The green highlight is intentionally identical in both themes: it is the brand
// selection colour, and the old dark map's blue-grey highlight was only there
// because no accent-on-dark value existed.
const ITEM_CLASS =
  'text-text-primary hover:bg-green hover:text-white data-highlighted:bg-green data-highlighted:text-white data-disabled:hover:bg-transparent data-disabled:data-highlighted:bg-transparent';

const DESTRUCTIVE_ITEM_CLASS =
  'text-red hover:bg-red/10 data-highlighted:bg-red/10 data-disabled:hover:bg-transparent';

export function DropdownMenu({ trigger, items }: DropdownMenuProps) {
  return (
    <RadixDropdownMenu.Root>
      <RadixDropdownMenu.Trigger asChild>{trigger}</RadixDropdownMenu.Trigger>
      <RadixDropdownMenu.Portal>
        <RadixDropdownMenu.Content
          align="end"
          sideOffset={8}
          className={`z-50 min-w-48 rounded-2xl p-2 ${CONTENT_CLASS}`}
        >
          {items.map((item) => (
            <RadixDropdownMenu.Item
              key={item.label}
              disabled={item.disabled}
              onSelect={() => item.onSelect()}
              className={`cursor-pointer rounded-xl px-4 py-2.5 text-p6 font-medium outline-none transition-colors data-disabled:cursor-not-allowed data-disabled:opacity-50 ${
                item.destructive ? DESTRUCTIVE_ITEM_CLASS : ITEM_CLASS
              }`}
            >
              {item.label}
            </RadixDropdownMenu.Item>
          ))}
        </RadixDropdownMenu.Content>
      </RadixDropdownMenu.Portal>
    </RadixDropdownMenu.Root>
  );
}
